import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, CheckCircle2, ArrowRight, ArrowLeft,
  Search, Shield, Zap, FileText, Image, Code
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const sections = [
  {
    title: 'Pre-Launch Technical Checks',
    icon: Code,
    items: [
      { task: 'Test all forms and contact submissions', description: 'Submit test entries through every form to verify data reaches the correct destination.' },
      { task: 'Verify all links work correctly', description: 'Click every internal and external link. Use a broken link checker tool for thoroughness.' },
      { task: 'Test on multiple browsers', description: 'Check Chrome, Firefox, Safari, and Edge. Each renders slightly differently.' },
      { task: 'Test on mobile devices', description: 'Use real phones and tablets, not just browser dev tools. Test both iOS and Android.' },
      { task: 'Check page load speed', description: 'Use Google PageSpeed Insights. Aim for scores above 80 on mobile and desktop.' },
      { task: 'Verify SSL certificate is installed', description: 'Your URL should show https:// and a padlock icon. No mixed content warnings.' },
      { task: 'Test 404 error page', description: 'Type a random URL on your domain to ensure your custom 404 page appears.' },
      { task: 'Check favicon displays correctly', description: 'Your site icon should appear in browser tabs and bookmarks.' },
    ]
  },
  {
    title: 'SEO Essentials',
    icon: Search,
    items: [
      { task: 'Add unique title tags to every page', description: 'Each page needs a distinct, descriptive title under 60 characters.' },
      { task: 'Write meta descriptions for all pages', description: 'Compelling summaries under 160 characters that encourage clicks from search results.' },
      { task: 'Implement proper heading hierarchy', description: 'One H1 per page, followed by H2s and H3s in logical order.' },
      { task: 'Add alt text to all images', description: 'Describe what each image shows for accessibility and SEO.' },
      { task: 'Create and submit XML sitemap', description: 'Generate a sitemap.xml and submit it to Google Search Console.' },
      { task: 'Set up robots.txt file', description: 'Control which pages search engines can crawl.' },
      { task: 'Implement canonical URLs', description: 'Prevent duplicate content issues by specifying the preferred URL for each page.' },
      { task: 'Add structured data markup', description: 'Help search engines understand your content with Schema.org markup.' },
    ]
  },
  {
    title: 'Content & Copy Review',
    icon: FileText,
    items: [
      { task: 'Proofread all text for spelling and grammar', description: 'Read everything aloud or use tools like Grammarly for a final check.' },
      { task: 'Verify contact information is correct', description: 'Triple-check phone numbers, email addresses, and physical addresses.' },
      { task: 'Ensure consistent brand voice', description: 'Your tone should match across all pages and align with your brand guidelines.' },
      { task: 'Check all CTAs are clear and actionable', description: 'Every call-to-action should tell visitors exactly what to do next.' },
      { task: 'Verify legal pages are in place', description: 'Privacy Policy, Terms of Service, and Cookie Policy if applicable.' },
      { task: 'Review copyright year in footer', description: 'Make sure it shows the current year and updates automatically.' },
    ]
  },
  {
    title: 'Images & Media',
    icon: Image,
    items: [
      { task: 'Optimize all images for web', description: 'Compress images to reduce file size without sacrificing quality. Use WebP format where possible.' },
      { task: 'Ensure images have correct dimensions', description: 'Don\'t rely on CSS to resize images. Upload them at the intended display size.' },
      { task: 'Test video embeds and players', description: 'Verify all videos load and play correctly, including on mobile.' },
      { task: 'Check image loading performance', description: 'Implement lazy loading for images below the fold.' },
      { task: 'Verify logo appears correctly everywhere', description: 'Check header, footer, favicon, and any email templates.' },
    ]
  },
  {
    title: 'Security & Performance',
    icon: Shield,
    items: [
      { task: 'Set up website backups', description: 'Configure automatic daily or weekly backups to a secure location.' },
      { task: 'Install security monitoring', description: 'Set up alerts for downtime, malware, or suspicious activity.' },
      { task: 'Configure caching properly', description: 'Enable browser caching and consider a CDN for faster global delivery.' },
      { task: 'Test form spam protection', description: 'Implement CAPTCHA or honeypot fields to prevent bot submissions.' },
      { task: 'Review user permissions', description: 'Ensure only authorized team members have admin access.' },
      { task: 'Enable HTTPS redirects', description: 'Force all HTTP traffic to redirect to HTTPS automatically.' },
    ]
  },
  {
    title: 'Analytics & Tracking',
    icon: Zap,
    items: [
      { task: 'Install Google Analytics 4', description: 'Set up GA4 with proper event tracking for key user actions.' },
      { task: 'Set up Google Search Console', description: 'Verify your site and monitor search performance.' },
      { task: 'Configure conversion tracking', description: 'Track form submissions, purchases, or other goal completions.' },
      { task: 'Test tracking is firing correctly', description: 'Use browser extensions to verify analytics code is working.' },
      { task: 'Set up custom dashboards', description: 'Create views for the metrics that matter most to your business.' },
    ]
  },
];

const GuideWebsiteLaunchPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-website-launch-page">
      <SEO 
        title="Website Launch Checklist - Complete Pre-Launch Guide"
        description="Everything you need to verify before launching your new website. A comprehensive checklist covering technical checks, SEO, content review, security, and analytics setup."
        keywords="website launch checklist, pre-launch checklist, website go-live checklist, website testing checklist, SEO checklist, website security checklist"
        url="/resources/guides/website-launch"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/resources/guides" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Guides
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6">
            <Globe className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Web Development</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-carbon-black mb-4" data-testid="guide-title">
            Website Launch Checklist
          </h1>
          <p className="text-xl text-slate-gray leading-relaxed">
            Everything you need to verify before launching your new website. From technical SEO to security checks, this comprehensive checklist ensures nothing falls through the cracks.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-slate-gray leading-relaxed">
              Launching a website is exciting, but it's also a critical moment where small oversights can lead to big problems. A broken contact form, missing meta tags, or slow load times can cost you visitors and credibility from day one.
            </p>
            <p className="text-slate-gray leading-relaxed">
              This checklist is designed to be worked through methodically in the days leading up to your launch. Print it out, assign team members to different sections, and check off each item as you verify it. Your future self will thank you.
            </p>
          </div>
        </div>
      </section>

      {/* Checklist Sections */}
      {sections.map((section, sectionIdx) => {
        const IconComponent = section.icon;
        return (
          <section key={sectionIdx} className={`py-12 sm:py-16 ${sectionIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black">{section.title}</h2>
              </div>
              
              <div className="space-y-4">
                {section.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        <CheckCircle2 className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-carbon-black mb-1">{item.task}</h3>
                        <p className="text-sm text-slate-gray leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Final Tips */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-6">Final Launch Day Tips</h2>
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-gray"><strong className="text-carbon-black">Launch during business hours</strong> - so your team is available to handle any issues that arise.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-gray"><strong className="text-carbon-black">Have a rollback plan</strong> - know exactly how to revert to the old site if something goes seriously wrong.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-gray"><strong className="text-carbon-black">Monitor closely for 48 hours</strong> - watch analytics, error logs, and form submissions closely after launch.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-gray"><strong className="text-carbon-black">Announce strategically</strong> - wait until you've verified everything works before promoting the new site.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Need Help With Your Launch?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Our team has launched hundreds of websites. Let us handle the technical details while you focus on your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/resources/consultation">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-blue-400 text-blue-600 hover:bg-blue-50 transition-all w-full sm:w-auto">
                More Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuideWebsiteLaunchPage;
